import { useLingui } from "@lingui/react/macro";
import { ModelData, calculateDurationH } from "@/lib/ModelDetails";

const COLOR_MODEL = "var(--secondary-purple-500)";
const COLOR_NORMATIVE = "var(--secondary-green-500)";

type Props = {
    model: ModelData;
};

/**
 * Renders a simple scatter plot with a linear regression line for the model and normative data.
 */
const HistoricalDataGraph = ({ model }: Props) => {
    const { t } = useLingui();

    if (!model.historicalData || model.historicalData.length === 0) {
        return (
            <div className="p-8 text-center text-gray-500">{t`No historical data available for visualization.`}</div>
        );
    }

    // Determine min/max values for axis scaling
    const allQuantities = model.historicalData.map(d => d.quantity);
    const allDurations = model.historicalData.map(d => d.duration);
    const maxQuantity = Math.max(...allQuantities, 0) * 1.1;
    const maxDuration = Math.max(...allDurations, 0) * 1.1;

    // Calculate model and normative line endpoints (for max quantity)
    const modelDurationHEnd = calculateDurationH(maxQuantity, model.modelSetup, model.modelSpeed);
    const normativeDurationHEnd = calculateDurationH(maxQuantity, model.normativeSetup, model.normativeSpeed);

    const chartHeight = 250;
    const chartWidth = 700;
    const padding = 40;

    const scaleX = (q: number) => (q / maxQuantity) * (chartWidth - 2 * padding) + padding;
    const scaleY = (d: number) => chartHeight - padding - (d / maxDuration) * (chartHeight - 2 * padding);

    // Points for the graph (normalized coordinates for SVG)
    const points = model.historicalData.map(d => ({
        cx: scaleX(d.quantity) || 0,
        cy: scaleY(d.duration) || 0,
    }));

    return (
        <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} width="100%" height="100%" className="chart-svg">
            {/* Axes */}
            <line
                x1={padding}
                y1={chartHeight - padding}
                x2={chartWidth - padding}
                y2={chartHeight - padding}
                stroke="#ccc"
            />
            <line x1={padding} y1={padding} x2={padding} y2={chartHeight - padding} stroke="#ccc" />

            {/* X-Axis Label */}
            <text x={chartWidth / 2} y={chartHeight - 5} textAnchor="middle" fontSize="12" fill="#555">
                {t`Quantity (Parts)`}
            </text>
            {/* Y-Axis Label */}
            <text
                x={10}
                y={chartHeight / 2}
                textAnchor="middle"
                fontSize="12"
                fill="#555"
                transform={`rotate(-90, 10, ${chartHeight / 2})`}
            >
                {t`Duration (min)`}
            </text>

            {/* Model Regression Line */}
            <line
                x1={scaleX(0)}
                y1={scaleY(model.modelSetup)} // Start at Q=0
                x2={scaleX(maxQuantity)}
                y2={scaleY(modelDurationHEnd)}
                stroke={COLOR_MODEL}
                strokeWidth="2"
                strokeDasharray="4 4"
            />

            {/* Normative Regression Line */}
            <line
                x1={scaleX(0)}
                y1={scaleY(model.normativeSetup)} // Start at Q=0
                x2={scaleX(maxQuantity)}
                y2={scaleY(normativeDurationHEnd)}
                stroke={COLOR_NORMATIVE}
                strokeWidth="2"
                opacity="0.7"
            />

            {/* Data Points */}
            {points.map((p, index) => (
                <circle key={index} cx={p.cx} cy={p.cy} r="4" fill={COLOR_MODEL} opacity="0.7">
                    <title>{`${t`Qty`}: ${model.historicalData[index].quantity}, ${t`Duration`}: ${model.historicalData[index].duration} ${t`min`}`}</title>
                </circle>
            ))}

            {/* Legend */}
            <g transform={`translate(${padding + 15}, ${padding})`} fontSize="12">
                <circle cx="5" cy="5" r="4" fill={COLOR_MODEL} />
                <text x="15" y="8" fill="#555">
                    {t`Historical Data`}
                </text>

                <line x1="0" y1="25" x2="10" y2="25" stroke={COLOR_MODEL} strokeWidth="2" strokeDasharray="4 4" />
                <text x="15" y="28" fill="#555">
                    {t`Model Prediction`}
                </text>

                <line x1="0" y1="45" x2="10" y2="45" stroke={COLOR_NORMATIVE} strokeWidth="2" opacity="0.7" />
                <text x="15" y="48" fill="#555">
                    {t`Normative Baseline`}
                </text>
            </g>
        </svg>
    );
};

export default HistoricalDataGraph;
